import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { publicStorefrontApi, StorefrontOrderStatus } from '../../services/storefrontService';
import { useStorefront } from '../../components/Storefront/StorefrontContext';
import { useStorefrontT } from '../../components/Storefront/useStorefrontT';

/** `/store/:slug/orders/:uuid?email=` — Ergebnis. UUID + E-Mail sind die zwei Faktoren. */
const StorefrontOrder: React.FC = () => {
    const { t } = useStorefrontT();
    const { uuid = '' } = useParams<{ uuid: string }>();
    const [params, setParams] = useSearchParams();
    const { slug } = useStorefront();
    const email = params.get('email') ?? '';
    const [input, setInput] = useState('');
    const [order, setOrder] = useState<StorefrontOrderStatus | null>(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!email) return;
        let cancelled = false;
        setNotFound(false);
        publicStorefrontApi
            .order(slug, uuid, email)
            .then((o) => !cancelled && setOrder(o))
            .catch(() => !cancelled && setNotFound(true));
        return () => {
            cancelled = true;
        };
    }, [slug, uuid, email]);

    if (!email) {
        return (
            <Box component="form" onSubmit={(e) => { e.preventDefault(); setParams({ email: input }); }} sx={{ maxWidth: 420 }}>
                <Typography gutterBottom>{t('order.emailPrompt')}</Typography>
                <Stack direction="row" spacing={1}>
                    <TextField size="small" type="email" fullWidth value={input} onChange={(e) => setInput(e.target.value)} />
                    <Button type="submit" variant="contained">{t('order.show')}</Button>
                </Stack>
            </Box>
        );
    }
    if (notFound) return <Alert severity="warning">{t('order.notFound')}</Alert>;
    if (!order) return <CircularProgress />;

    const done = order.status === 'paid' || order.status === 'completed';
    const failed = order.status === 'payment_failed' || order.status === 'cancelled';
    return (
        <Box>
            <Typography variant="h4" component="h1" gutterBottom>{done ? t('order.thanks') : t('order.title')}</Typography>
            {done ? <Alert severity="success" sx={{ mb: 2 }}>{t('order.thanksBody')}</Alert> : null}
            {failed ? <Alert severity="error" sx={{ mb: 2 }}>{t('order.failed')}</Alert> : null}
            {!done && !failed ? <Alert severity="info" sx={{ mb: 2 }}>{t('order.pending')}</Alert> : null}
            <Typography color="text.secondary" sx={{ mb: 3 }}>{t('order.number')}: {order.orderNumber}</Typography>
            {done ? <Button variant="contained" href="/">{t('order.toLogin')}</Button> : null}
        </Box>
    );
};

export default StorefrontOrder;
